import React, { useState } from 'react';
import { 
  Box,
  Button,
} from '@mui/material';
import { 
  Language as LanguageIcon,
  Refresh as RefreshIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getKnowledgeBase, addKnowledgeBase, createDocument, deleteKnowledgeBase, getKnowledgeBaseById } from '../../redux/actions/knowledgeBaseActions';
import useCallOnce from '../../hooks/useCallOnce';
import { showSuccess, showError } from '../../Components/reuse/Toast';
import LinearLoading from '../../Components/reuse/LinearLoading';
import SearchBar from './components/SearchBar';
import KnowledgeBaseTable from './components/KnowledgeBaseTable';
import AddUrlDialog from './components/AddUrlDialog';
import AddTextDialog from './components/AddTextDialog';
import ContextMenu from './components/ContextMenu';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import DocumentDetailSidebar from './components/DocumentDetailSidebar';

function KnowledgeBase() {
  const dispatch = useDispatch();
  const { loading, knowledgeBases } = useSelector((state) => state.knowledgeBase);
  const [isAdding, setIsAdding] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', url: '', type: 'url' });
  const [formErrors, setFormErrors] = useState({});
  
  // Text dialog state
  const [isCreatingText, setIsCreatingText] = useState(false);
  const [openTextDialog, setOpenTextDialog] = useState(false);
  const [textFormData, setTextFormData] = useState({ name: '', text: '', type: 'text' });
  const [textFormErrors, setTextFormErrors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [documentDetailOpen, setDocumentDetailOpen] = useState(false);

  // Fetch knowledge base data when component mounts
  useCallOnce('getKnowledgeBase', () => {
    dispatch(getKnowledgeBase());
  }, [dispatch]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormData({ name: '', url: '', type: 'url' });
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', url: '', type: 'url' });
    setFormErrors({});
  };

  // Text dialog handlers
  const handleOpenTextDialog = () => {
    setOpenTextDialog(true);
    setTextFormData({ name: '', text: '', type: 'text' });
    setTextFormErrors({});
  };

  const handleCloseTextDialog = () => {
    setOpenTextDialog(false);
    setTextFormData({ name: '', text: '', type: 'text' });
    setTextFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.url)) {
        errors.url = 'Please enter a valid URL starting with http:// or https://';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTextForm = () => {
    const errors = {};
    
    if (!textFormData.name.trim()) {
      errors.name = 'Text name is required';
    }
    
    if (!textFormData.text.trim()) {
      errors.text = 'Text content is required';
    }
    
    setTextFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsAdding(true);
    try {
      const response = await dispatch(addKnowledgeBase(formData));
      
      if (response?.success) {
        showSuccess(response.message || 'Knowledge base added successfully!');
        handleCloseDialog();
        // Refresh the list
        dispatch(getKnowledgeBase());
      }
    } catch (error) {
      console.error('Failed to add knowledge base:', error);
      
      // Show error toast with specific error message
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to add knowledge base. Please try again.';
      showError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  const handleTextSubmit = async () => {
    if (!validateTextForm()) return;

    setIsCreatingText(true);
    try {
      const response = await dispatch(createDocument(textFormData));
      
      if (response?.success) {
        showSuccess(response.message || 'Text document created successfully!');
        handleCloseTextDialog();
        // Refresh the list
        dispatch(getKnowledgeBase());
      }
    } catch (error) {
      console.error('Failed to create text document:', error);
      
      // Show error toast with specific error message
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to create text document. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCreatingText(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleTextChange = (field) => (event) => {
    setTextFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (textFormErrors[field]) {
      setTextFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    // Don't clear selectedItem here, keep it for the delete operation
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const response = await dispatch(deleteKnowledgeBase(selectedItem.knowledgeBaseId));
      
      if (response?.success) {
        showSuccess(response.message || 'Document deleted successfully!');
        setDeleteDialogOpen(false);
        setSelectedItem(null);
        // Refresh the list to get latest data
        dispatch(getKnowledgeBase());
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to delete document. Please try again.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDocumentClick = async (item) => {
    setDocumentDetailOpen(true);
    try {
      // The loading state is managed by Redux action
      await dispatch(getKnowledgeBaseById(item.knowledgeBaseId));
    } catch (error) {
      console.error('Failed to fetch document details:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to fetch document details. Please try again.';
      showError(errorMessage);
      // Close the sidebar on error
      setDocumentDetailOpen(false);
    }
  };

  // Filter knowledge bases based on search term
  const filteredKnowledgeBases = knowledgeBases.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.knowledgeBaseId && item.knowledgeBaseId.toLowerCase().includes(searchLower)) ||
      (item.knowledgeBaseUrl && item.knowledgeBaseUrl.toLowerCase().includes(searchLower)) ||
      (item.userId && item.userId.toLowerCase().includes(searchLower))
    );
  });

  return (
    <Box sx={{ width: '100%' }}>
      {/* Action Buttons Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        mb: 3,
        gap: 2
      }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => dispatch(getKnowledgeBase())}
          disabled={loading}
          sx={{
            backgroundColor: '#ffffff',
            borderColor: '#d1d5db',
            color: '#374151',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 500,
            '&:hover': { 
              backgroundColor: '#f9fafb',
              borderColor: '#9ca3af'
            },
            '&:disabled': {
              backgroundColor: '#f9fafb',
              borderColor: '#e5e7eb',
              color: '#9ca3af'
            }
          }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<LanguageIcon />}
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: '#374151',
            color: '#ffffff',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            border: '1px solid #000000',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '&:hover': { 
              backgroundColor: '#1f2937',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            },
            '&:active': {
              backgroundColor: '#111827',
              transform: 'translateY(1px)'
            }
          }}
        >
          Add URL
        </Button>
        <Button
          variant="contained"
          startIcon={<TextFieldsIcon />}
          onClick={handleOpenTextDialog}
          sx={{
            backgroundColor: '#374151',
            color: '#ffffff',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            border: '1px solid #000000',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '&:hover': { 
              backgroundColor: '#1f2937',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            },
            '&:active': {
              backgroundColor: '#111827',
              transform: 'translateY(1px)'
            }
          }}
        >
          Create Text
        </Button>
      </Box>

        {loading && <LinearLoading show={true} />}

        {/* Search Bar */}
        <SearchBar 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredCount={filteredKnowledgeBases.length}
          totalCount={knowledgeBases.length}
        />

        {/* Knowledge Base Table */}
        <KnowledgeBaseTable 
          filteredKnowledgeBases={filteredKnowledgeBases}
          searchTerm={searchTerm}
          onDocumentClick={handleDocumentClick}
          onMenuOpen={handleMenuOpen}
        />

        {/* Add URL Dialog */}
        <AddUrlDialog 
          open={openDialog}
          onClose={handleCloseDialog}
          formData={formData}
          formErrors={formErrors}
          isAdding={isAdding}
          onChange={handleChange}
          onSubmit={handleSubmit}
        />

        {/* Add Text Dialog */}
        <AddTextDialog 
          open={openTextDialog}
          onClose={handleCloseTextDialog}
          formData={textFormData}
          formErrors={textFormErrors}
          isAdding={isCreatingText}
          onChange={handleTextChange}
          onSubmit={handleTextSubmit}
        />

        {/* Context Menu */}
        <ContextMenu 
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onCopyId={() => {
            if (selectedItem) {
              navigator.clipboard.writeText(selectedItem.knowledgeBaseId);
              showSuccess('Document ID copied to clipboard!');
              handleMenuClose();
            }
          }}
          onDeleteClick={handleDeleteClick}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog 
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          selectedItem={selectedItem}
          isDeleting={isDeleting}
          onConfirm={handleDeleteConfirm}
        />

        {/* Document Detail Sidebar */}
        <DocumentDetailSidebar 
          open={documentDetailOpen}
          onClose={() => setDocumentDetailOpen(false)}
        />
      </Box>
    );
}

export default KnowledgeBase;
